package ru.villawonka.app.presentation.design.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.ui.tooling.preview.Preview
import ru.villawonka.app.presentation.design.extensions.AppSpacer
import ru.villawonka.app.presentation.design.theme.AppColors
import ru.villawonka.app.presentation.design.theme.AppTextStyles
import ru.villawonka.app.presentation.design.theme.AppTheme
import villawonka.composeapp.generated.resources.Res
import villawonka.composeapp.generated.resources.ic_plus_24

@Composable
fun AppButton(
    modifier: Modifier = Modifier,
    text: String,
    icon: DrawableResource? = null,
    enabled: Boolean = true,
    type: AppButtonType,
    size: AppButtonSize,
    onClick: () -> Unit,
) {
    val contentPadding = when (size) {
        AppButtonSize.LARGE ->
            if (icon == null) {
                PaddingValues(24.dp)
            } else {
                PaddingValues(
                    start = 16.dp,
                    end = 24.dp,
                    top = 24.dp,
                    bottom = 24.dp
                )
            }
        AppButtonSize.MEDIUM ->
            if (icon == null) {
                PaddingValues(
                    horizontal = 24.dp,
                    vertical = 12.dp
                )
            } else {
                PaddingValues(
                    start = 16.dp,
                    end = 24.dp,
                    top = 12.dp,
                    bottom = 12.dp
                )
            }
    }
    val colors = when (type) {
        AppButtonType.PRIMARY -> ButtonDefaults.buttonColors(
            backgroundColor = MaterialTheme.colors.filledButtonPrimaryBackgroundEnabled,
            contentColor = MaterialTheme.colors.filledButtonPrimaryContentEnabled,
            disabledBackgroundColor = MaterialTheme.colors.filledButtonPrimaryBackgroundDisabled,
            disabledContentColor = MaterialTheme.colors.filledButtonPrimaryContentDisabled,
        )
        AppButtonType.SECONDARY -> ButtonDefaults.buttonColors(
            backgroundColor = MaterialTheme.colors.filledButtonSecondaryBackgroundEnabled,
            contentColor = MaterialTheme.colors.filledButtonSecondaryContentEnabled,
            disabledBackgroundColor = MaterialTheme.colors.filledButtonSecondaryBackgroundDisabled,
            disabledContentColor = MaterialTheme.colors.filledButtonSecondaryContentDisabled,
        )
        AppButtonType.TERTIARY -> ButtonDefaults.buttonColors(
            backgroundColor = MaterialTheme.colors.filledButtonTertiaryBackgroundEnabled,
            contentColor = MaterialTheme.colors.filledButtonTertiaryContentEnabled,
            disabledBackgroundColor = MaterialTheme.colors.filledButtonTertiaryBackgroundDisabled,
            disabledContentColor = MaterialTheme.colors.filledButtonTertiaryContentDisabled,
        )
    }

    Button(
        onClick = onClick,
        enabled = enabled,
        shape = RoundedCornerShape(12.dp),
        contentPadding = contentPadding,
        elevation = ButtonDefaults.elevation(
            defaultElevation = 0.dp,
            pressedElevation = 0.dp,
            disabledElevation = 0.dp,
            hoveredElevation = 0.dp,
            focusedElevation = 0.dp
        ),
        colors = colors,
        modifier = modifier
    ) {
        icon?.let {
            Icon(
                painter = painterResource(it),
                contentDescription = text,
                tint = colors.contentColor(enabled).value
            )
            AppSpacer(10.dp)
        }
        Text(
            text = text,
            style = AppTextStyles.labelLargeMedium
        )
    }
}


/**
 * Primary Content
 */

val Colors.filledButtonPrimaryContentInverted
    get() = if (isLight) AppColors.violet700 else AppColors.grayLight700

val Colors.filledButtonPrimaryContentEnabled
    get() = if (isLight) AppColors.grayLight50 else AppColors.grayDark50

val Colors.filledButtonPrimaryContentHovered
    get() = if (isLight) AppColors.grayLight50 else AppColors.grayDark50

val Colors.filledButtonPrimaryContentFocused
    get() = if (isLight) AppColors.grayLight50 else AppColors.grayDark50

val Colors.filledButtonPrimaryContentSelected
    get() = if (isLight) AppColors.grayLight50 else AppColors.grayDark50

val Colors.filledButtonPrimaryContentDisabled
    get() = if (isLight) AppColors.grayLight600 else AppColors.grayDark500


/**
 * Primary Background
 */

val Colors.filledButtonPrimaryBackgroundEnabled
    get() = if (isLight) AppColors.violet700 else AppColors.violet700

val Colors.filledButtonPrimaryBackgroundHovered
    get() = if (isLight) AppColors.violet800 else AppColors.violet800

val Colors.filledButtonPrimaryBackgroundFocused
    get() = if (isLight) AppColors.violet900 else AppColors.violet900

val Colors.filledButtonPrimaryBackgroundSelected
    get() = if (isLight) AppColors.violet950 else AppColors.violet950

val Colors.filledButtonPrimaryBackgroundDisabled
    get() = if (isLight) AppColors.grayLight900 else AppColors.grayDark700


/**
 * Secondary Content
 */

val Colors.filledButtonSecondaryContentInverted
    get() = if (isLight) AppColors.blue500 else AppColors.blue500

val Colors.filledButtonSecondaryContentEnabled
    get() = if (isLight) AppColors.grayLight50 else AppColors.grayDark50

val Colors.filledButtonSecondaryContentHovered
    get() = if (isLight) AppColors.grayLight50 else AppColors.grayDark50

val Colors.filledButtonSecondaryContentFocused
    get() = if (isLight) AppColors.grayLight50 else AppColors.grayDark50

val Colors.filledButtonSecondaryContentSelected
    get() = if (isLight) AppColors.grayLight50 else AppColors.grayDark50

val Colors.filledButtonSecondaryContentDisabled
    get() = if (isLight) AppColors.grayLight600 else AppColors.grayDark500


/**
 * Secondary Background
 */

val Colors.filledButtonSecondaryBackgroundEnabled
    get() = if (isLight) AppColors.blue500 else AppColors.blue500

val Colors.filledButtonSecondaryBackgroundHovered
    get() = if (isLight) AppColors.blue600 else AppColors.blue600

val Colors.filledButtonSecondaryBackgroundFocused
    get() = if (isLight) AppColors.blue700 else AppColors.blue700

val Colors.filledButtonSecondaryBackgroundSelected
    get() = if (isLight) AppColors.blue800 else AppColors.blue800

val Colors.filledButtonSecondaryBackgroundDisabled
    get() = if (isLight) AppColors.grayLight900 else AppColors.grayDark700


/**
 * Tertiary Content
 */

val Colors.filledButtonTertiaryContentEnabled
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.filledButtonTertiaryContentHovered
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.filledButtonTertiaryContentFocused
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.filledButtonTertiaryContentSelected
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.filledButtonTertiaryContentDisabled
    get() = if (isLight) AppColors.grayLight700 else AppColors.grayDark500


/**
 * Tertiary Background
 */

val Colors.filledButtonTertiaryBackgroundEnabled
    get() = if (isLight) AppColors.grayLight200 else AppColors.grayDark600

val Colors.filledButtonTertiaryBackgroundHovered
    get() = if (isLight) AppColors.grayLight300 else AppColors.grayDark700

val Colors.filledButtonTertiaryBackgroundFocused
    get() = if (isLight) AppColors.grayLight400 else AppColors.grayDark800

val Colors.filledButtonTertiaryBackgroundSelected
    get() = if (isLight) AppColors.grayLight300 else AppColors.grayDark500

val Colors.filledButtonTertiaryBackgroundDisabled
    get() = if (isLight) AppColors.grayLight900 else AppColors.grayDark600


/**
 * Preview: Dark Theme
 */

@Preview
@Composable
private fun PreviewInDarkTheme() {
    Column {
        AppTheme(isDarkTheme = true) {
            Column(
                modifier = Modifier
                    .background(color = Color.Black)
                    .padding(16.dp)
            ) {
                AppButton(
                    text = "Primary large",
                    type = AppButtonType.PRIMARY,
                    size = AppButtonSize.LARGE
                ) {}
                AppSpacer(12.dp)
                AppButton(
                    text = "Primary medium",
                    type = AppButtonType.PRIMARY,
                    size = AppButtonSize.MEDIUM
                ) {}
                AppSpacer(12.dp)
                AppButton(
                    text = "Secondary large",
                    type = AppButtonType.SECONDARY,
                    size = AppButtonSize.LARGE
                ) {}
                AppSpacer(12.dp)
                AppButton(
                    text = "Secondary medium",
                    type = AppButtonType.SECONDARY,
                    size = AppButtonSize.MEDIUM
                ) {}
                AppSpacer(12.dp)
                AppButton(
                    text = "Tertiary large",
                    type = AppButtonType.TERTIARY,
                    size = AppButtonSize.LARGE
                ) {}
                AppSpacer(12.dp)
                AppButton(
                    text = "Tertiary medium",
                    type = AppButtonType.TERTIARY,
                    size = AppButtonSize.MEDIUM
                ) {}
            }
        }
    }
}

@Preview
@Composable
private fun PreviewInDarkThemeWithIcon() {
    AppTheme(isDarkTheme = true) {
        Column(
            modifier = Modifier
                .background(color = Color.Black)
                .padding(16.dp)
        ) {
            AppButton(
                text = "Primary large",
                icon = Res.drawable.ic_plus_24,
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Primary medium",
                icon = Res.drawable.ic_plus_24,
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Secondary large",
                icon = Res.drawable.ic_plus_24,
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Secondary medium",
                icon = Res.drawable.ic_plus_24,
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Tertiary large",
                icon = Res.drawable.ic_plus_24,
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Tertiary medium",
                icon = Res.drawable.ic_plus_24,
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.MEDIUM
            ) {}
        }
    }
}

/**
 * Preview: Light Theme
 */

@Preview
@Composable
private fun PreviewInLightTheme() {
    AppTheme(isDarkTheme = false) {
        Column(
            modifier = Modifier
                .background(color = Color.White)
                .padding(16.dp)
        ) {
            AppButton(
                text = "Primary large",
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Primary medium",
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Secondary large",
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Secondary medium",
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Tertiary large",
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Tertiary medium",
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.MEDIUM
            ) {}
        }
    }
}

@Preview
@Composable
private fun PreviewInLightThemeWithIcon() {
    AppTheme(isDarkTheme = false) {
        Column(
            modifier = Modifier
                .background(color = Color.White)
                .padding(16.dp)
        ) {
            AppButton(
                text = "Primary large",
                icon = Res.drawable.ic_plus_24,
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Primary medium",
                icon = Res.drawable.ic_plus_24,
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Secondary large",
                icon = Res.drawable.ic_plus_24,
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Secondary medium",
                icon = Res.drawable.ic_plus_24,
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Tertiary large",
                icon = Res.drawable.ic_plus_24,
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Tertiary medium",
                icon = Res.drawable.ic_plus_24,
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.MEDIUM
            ) {}
        }
    }
}

@Preview
@Composable
private fun PreviewInLightThemeDisabled() {
    AppTheme(isDarkTheme = false) {
        Column(
            modifier = Modifier
                .background(color = Color.White)
                .padding(16.dp)
        ) {
            AppButton(
                text = "Primary large",
                enabled = false,
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Primary medium",
                enabled = false,
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Secondary large",
                enabled = false,
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Secondary medium",
                enabled = false,
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Tertiary large",
                enabled = false,
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Tertiary medium",
                enabled = false,
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.MEDIUM
            ) {}
        }
    }
}

@Preview
@Composable
private fun PreviewInLightThemeWithIconDisabled() {
    AppTheme(isDarkTheme = false) {
        Column(
            modifier = Modifier
                .background(color = Color.White)
                .padding(16.dp)
        ) {
            AppButton(
                text = "Primary large",
                enabled = false,
                icon = Res.drawable.ic_plus_24,
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Primary medium",
                enabled = false,
                icon = Res.drawable.ic_plus_24,
                type = AppButtonType.PRIMARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Secondary large",
                enabled = false,
                icon = Res.drawable.ic_plus_24,
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Secondary medium",
                enabled = false,
                icon = Res.drawable.ic_plus_24,
                type = AppButtonType.SECONDARY,
                size = AppButtonSize.MEDIUM
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Tertiary large",
                enabled = false,
                icon = Res.drawable.ic_plus_24,
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.LARGE
            ) {}
            AppSpacer(12.dp)
            AppButton(
                text = "Tertiary medium",
                enabled = false,
                icon = Res.drawable.ic_plus_24,
                type = AppButtonType.TERTIARY,
                size = AppButtonSize.MEDIUM
            ) {}
        }
    }
}